/**
 * The BUTTONS explore page, shared by all three report trees.
 *
 * One row per `BTN_*` channel PRESSED IN THE LAST 15 MINUTES: the channel name, an LED for its state at the
 * cursor, and a step sparkline of its last 20 seconds. Most recently pressed first. The list scrolls and is
 * filtered by a search box above it.
 *
 * The 15 minute rule is the whole row-selection policy and is not optional. A boat carries ~114 buttons and
 * almost all are idle almost all the time, so listing them all buried the handful that mattered in a wall of
 * dark LEDs. There was briefly an "Active only" checkbox for this; it was removed because unticking it only
 * ever restored that wall. The server still accepts `active_only=0` as a diagnostic lever (see
 * liveButtons.js) - nothing in the UI sends it.
 *
 * `variant='single'` (dataset) is one boat, so there is no source selector.
 * `variant='fleet'`  (day, live) adds one, because a date or a live session has several boats and the table
 *                    shows one at a time.
 *
 * WHERE THE DATA COMES FROM, and why it is not like Alarms. `BTN_*` lives in InfluxDB only — it is in no
 * parquet and the boat stream never subscribes to it. Live therefore reads the stream server's rolling
 * window (GET /api/stream/buttons/live) only while this page is mounted, which is what keeps the server
 * poll running; dataset and day read a window around the playhead. Both passes send the project and
 * the date, so the first Timescale read is written to parquet and the next read of that window comes
 * from the file.
 * Both normalise to the same column shape in
 * buttonsService, so everything below this comment has a single code path.
 *
 * LIVE POLLS, HISTORICAL SCRUBS. Live re-reads every 10s and pins the cursor to the newest sample it has.
 * Dataset and day load once and take their cursor from the playhead, so scrubbing the timeline moves every
 * LED and sparkline together. That split is the whole reason `live` is a prop rather than something sniffed
 * from the store.
 */

import { onMount, onCleanup, createSignal, createMemo, createEffect, untrack, Show, For } from 'solid-js';
import { FiSearch, FiRefreshCw } from 'solid-icons/fi';
import { persistantStore } from '../../store/persistantStore';
import { selectedTime, isPlaying } from '../../store/playbackStore';
import { getTimezoneForDate } from '../../utils/global';
import { error as logError, debug } from '../../utils/console';
import { telemetryStoreLabel } from '../../utils/telemetryStore';
import { logPageLoad } from '../../utils/logging';
import { fetchSourcesForDate, type DateSource } from '../../services/alarmsService';
import {
  fetchButtonRange,
  fetchLiveButtons,
  rangeForCursor,
  rangeCovers,
  valueAt,
  windowSlice,
  ledStateFor,
  engagedForMs,
  lastPressedMs,
  ENGAGED_WARN_MS,
  ENGAGED_ALERT_MS,
  type ButtonLedState,
  EMPTY_BUTTON_DATA,
  SPARKLINE_WINDOW_MS,
  type ButtonData,
  type ButtonRange,
} from '../../services/buttonsService';
import ButtonSparkline from './ButtonSparkline';

const { selectedClassName, selectedProjectId, selectedDatasetId, selectedDate, selectedSourceName } =
  persistantStore;

/**
 * Live re-read cadence. Matches the stream server's own 10s Influx poll (liveButtons.js): asking faster
 * cannot surface anything new, because nothing reaches the rolling window until that poll lands.
 */
const LIVE_REFRESH_MS = 10_000;

export interface ButtonsReportSectionProps {
  variant?: 'single' | 'fleet';
  /** Live mode: poll the stream server instead of reading the day, and pin the cursor to the newest sample. */
  live?: boolean;
  [key: string]: unknown;
}

export default function ButtonsReportSection(props: ButtonsReportSectionProps) {
  const isFleet = () => props.variant === 'fleet';
  const isLive = () => props.live === true;

  const [data, setData] = createSignal<ButtonData>(EMPTY_BUTTON_DATA);
  const [sources, setSources] = createSignal<string[]>([]);
  const [chosenSource, setChosenSource] = createSignal('');
  const [loading, setLoading] = createSignal(true);
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [searchTerm, setSearchTerm] = createSignal('');
  /**
   * What the page is currently waiting on, shown next to the spinner and logged.
   *
   * The day path can sit for a long time on ONE of two calls - channel discovery or the value read - and
   * both hit production Influx for a whole day. Without this the page shows an identical "Loading buttons…"
   * for a 200ms hit and a two-minute Influx scan, so there is no way to tell a slow query from a hang.
   */
  const [stage, setStage] = createSignal('starting');
  /**
   * A read is in flight. Mirrors the `inFlight` flag, which is a plain variable because the poll tick reads
   * it outside any reactive scope - the signal exists purely so the toolbar can show a spinner.
   *
   * Distinct from `loading`: that one is the first load of a context and replaces the table, this one is a
   * refresh happening UNDER a table that still shows the previous data.
   */
  const [busy, setBusy] = createSignal(false);

  let abortController: AbortController | null = null;
  /**
   * True while a read is outstanding, so the live poll skips a tick rather than cancelling it.
   *
   * Learned from AlarmsReportSection: a tick that aborts the read in flight turns into a cancel loop the
   * moment the read takes longer than the interval, and the table then freezes on its first payload for the
   * rest of the session.
   */
  let inFlight = false;
  /** After the first successful load a poll updates in place, rather than blanking the table every 10s. */
  let hasLoadedOnce = false;
  /** Bumped per load so a late response from a previous context cannot overwrite a newer one. */
  let fetchGeneration = 0;
  /** The window the held data covers (non-live). Null until the first range read lands. */
  let loadedRange: ButtonRange | null = null;

  /* ---- context ----------------------------------------------------------------------------- */

  /** UTC YYYYMMDD. Live has no selected date, and the Influx day is UTC, so today-UTC is the right default. */
  const todayUtcYmd = () => {
    const d = new Date();
    return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}`;
  };
  const dateYmd = () => {
    const selected = String(selectedDate?.() ?? '').replace(/[-/]/g, '').slice(0, 8);
    return selected || todayUtcYmd();
  };

  /** The boat to show: the dropdown in fleet mode, the dataset's own boat otherwise. */
  const effectiveSource = () => (isFleet() ? chosenSource() : String(selectedSourceName?.() ?? ''));

  /**
   * Everything the read depends on, in one tracked place.
   *
   * A change HERE invalidates the read in flight — it is for a different boat or day — so that one is
   * aborted. A live poll is not a context change and must not abort anything; see onMount.
   */
  const loadContext = createMemo(() => ({
    className: selectedClassName?.(),
    projectId: selectedProjectId?.(),
    fleet: isFleet(),
    ymd: dateYmd(),
    datasetId: selectedDatasetId?.(),
    sourceName: effectiveSource(),
    live: isLive(),
  }));

  type LoadContext = ReturnType<typeof loadContext>;

  /* ---- source list ------------------------------------------------------------------------- */

  // Day/fleet: the boats that actually have a dataset on this date. Live fills this from the poll instead,
  // so the dropdown can never offer a boat the stream has no buttons for.
  createEffect(() => {
    if (!isFleet() || isLive()) {return;}
    const className = selectedClassName?.();
    const projectId = selectedProjectId?.();
    const ymd = dateYmd();
    if (!className || projectId == null || !ymd) {return;}

    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });

    void (async () => {
      try {
        const tz = (await getTimezoneForDate(className, Number(projectId), ymd).catch(() => null)) || 'UTC';
        const list: DateSource[] = await fetchSourcesForDate(className, Number(projectId), ymd, tz);
        if (cancelled) {return;}
        const names = list.map((s) => s.source_name);
        setSources(names);
        // Keep the current pick if it survives the date change; otherwise fall to the first boat.
        if (!names.includes(untrack(chosenSource))) {setChosenSource(names[0] ?? '');}
      } catch (err) {
        if (!cancelled) {debug('[buttons] source list failed', err);}
      }
    })();
  });

  /* ---- load -------------------------------------------------------------------------------- */

  /** Read one context. Reads no signals, so a timer can call it. */
  const runLoad = (ctx: LoadContext & { cursorMs: number }) => {
    const { className, projectId, fleet, ymd, sourceName, live } = ctx;

    // Live does not need a source to make its first call — that call is what discovers the boats.
    if (!live) {
      // EVERY early return must clear `loading`. It starts true, and a return that leaves it set strands
      // the page on "Loading buttons…" for ever with nothing in the console to say why - which is exactly
      // what this branch did when the project/class context was not ready on first run.
      if (!className || projectId == null) {
        setStage('waiting for project');
        setLoading(false);
        return;
      }
      if (fleet ? !ymd || !sourceName : !sourceName) {
        setData(EMPTY_BUTTON_DATA);
        setStage(fleet ? 'waiting for source' : 'waiting for dataset');
        setLoading(false);
        return;
      }
    }

    const gen = ++fetchGeneration;
    const controller = new AbortController();
    abortController = controller;
    const signal = controller.signal;
    inFlight = true;
    setBusy(true);

    if (!hasLoadedOnce) {setLoading(true);}
    setLoadError(null);
    debug('[buttons] load start', { live, fleet, ymd, sourceName, gen });

    void (async () => {
      try {
        if (live) {
          setStage('polling stream server');
          const payload = await fetchLiveButtons({
            sourceName: sourceName || undefined,
            signal,
            projectId,
            className,
            dateYmd: ymd,
          });
          if (signal.aborted || gen !== fetchGeneration) {return;}
          setSources(payload.sources);
          // First poll has no boat picked yet; adopt the first the server reports. The next poll narrows to
          // it, so the wide response only ever happens once.
          if (!sourceName && payload.sources.length > 0) {setChosenSource(payload.sources[0]);}
          setData({ ts: payload.ts, channels: payload.channels, values: payload.values });
          debug('[buttons] live poll', { channels: payload.channels.length, buckets: payload.ts.length });
          return;
        }

        // A WINDOW AROUND THE PLAYHEAD, not the whole day - see fetchButtonRange for why a day is
        // ~61 million points and always 504s.
        const range = rangeForCursor(ctx.cursorMs);
        setStage(`reading ${telemetryStoreLabel()} window`);
        const payload = await fetchButtonRange({
          sourceName,
          startMs: range.startMs,
          endMs: range.endMs,
          cursorMs: ctx.cursorMs,
          signal,
          projectId,
          className,
          dateYmd: ymd,
        });
        if (signal.aborted || gen !== fetchGeneration) {return;}

        loadedRange = range;
        // Deliberately NOT setSources here. The range read is filtered to ONE boat, so its `sources` is
        // that boat alone - assigning it would collapse the day/fleet dropdown to a single entry after
        // the first load. In fleet mode the dropdown is owned by the fetchSourcesForDate effect above,
        // and dataset mode has no dropdown at all.
        setData({ ts: payload.ts, channels: payload.channels, values: payload.values });
        debug('[buttons] window loaded', {
          channels: payload.channels.length,
          buckets: payload.ts.length,
          sourceName,
          from: new Date(range.startMs).toISOString(),
          to: new Date(range.endMs).toISOString(),
        });
      } catch (err) {
        if (signal.aborted || gen !== fetchGeneration) {return;}
        logError('[buttons] load failed', err);
        setLoadError((err as Error)?.message || 'Failed to load buttons');
        setData(EMPTY_BUTTON_DATA);
      } finally {
        // Only the CURRENT read may clear these: an aborted predecessor settles after its replacement has
        // started, and clearing `inFlight` there would re-open the poll on a read that is still running.
        if (gen === fetchGeneration) {
          inFlight = false;
          setBusy(false);
          hasLoadedOnce = true;
          setLoading(false);
        }
      }
    })();
  };

  /** Signature of the last context loaded, so a cursor move can be told from a boat/day change. */
  let lastContextKey = '';

  createEffect(() => {
    const ctx = loadContext();
    const contextKey = JSON.stringify(ctx);
    const contextChanged = contextKey !== lastContextKey;
    if (contextChanged) {
      lastContextKey = contextKey;
      // Different boat or day: whatever window we hold is for something else.
      loadedRange = null;
    }

    // Live is driven by its own 10s interval and must not track the cursor at all - reading cursorMs()
    // here would subscribe this effect to the live edge and re-fire it on every poll.
    const cursor = ctx.live ? NaN : cursorMs();

    if (!ctx.live) {
      if (!Number.isFinite(cursor)) {
        setStage('waiting for a time selection');
        setLoading(false);
        return;
      }
      // The held window still covers the sparkline, so a small scrub costs nothing. This is what keeps
      // dragging the playhead from firing an Influx query per pixel.
      if (!contextChanged && rangeCovers(loadedRange, cursor)) {return;}
    }

    abortController?.abort();
    abortController = null;
    inFlight = false;
    setBusy(false);
    runLoad({ ...ctx, cursorMs: cursor });
  });

  onMount(() => {
    void logPageLoad('ButtonsReportSection.tsx', 'Buttons', 'Loaded');
    if (!isLive()) {return;}
    const timer = setInterval(() => {
      if (inFlight) {return;}
      runLoad({ ...untrack(loadContext), cursorMs: NaN });
    }, LIVE_REFRESH_MS);
    onCleanup(() => clearInterval(timer));
  });

  onCleanup(() => {
    fetchGeneration += 1;
    abortController?.abort();
  });

  /* ---- cursor ------------------------------------------------------------------------------ */

  /**
   * Where the LEDs and sparklines are read.
   *
   * Live pins to the newest sample actually held rather than to wall-clock now: the poll is 10s and the
   * stream's own poll is another 10s, so "now" is routinely ahead of the freshest bucket, and anchoring there
   * would slide every sparkline off its right edge between polls.
   */
  const cursorMs = createMemo<number>((prev) => {
    if (isLive()) {
      const ts = data().ts;
      return ts.length > 0 ? ts[ts.length - 1] : Date.now();
    }

    // FROZEN WHILE THE CLOCK RUNS, by request: the LEDs track scrubbing but not animation.
    //
    // Returning here without reading selectedTime() also drops it from this memo's dependency set, so
    // Solid stops re-running the memo altogether while playing - and with it `rows` and the windowed
    // read below. The freeze removes the work rather than hiding it. When playback stops the memo runs
    // again, reads selectedTime and resumes tracking it.
    //
    // No seed argument to createMemo: eslint-plugin-solid's no-react-deps rule crashes on a second
    // argument here, reading it as a React dependency array. `prev` is therefore possibly undefined on
    // the first run, which this guard handles anyway.
    if (isPlaying() && prev !== undefined && Number.isFinite(prev)) {return prev;}

    const t = selectedTime();
    const ms = t instanceof Date ? t.getTime() : Number(t);

    // Deliberately NO fallback to the newest loaded sample: this cursor decides WHICH window gets
    // loaded, so deriving it from the loaded data would be circular. No playhead means no window.
    return Number.isFinite(ms) ? ms : NaN;
  });

  /* ---- filter ------------------------------------------------------------------------------ */

  /** Multi-term AND match, the same rule the channel pickers use — "btn port" matches BTN_PORT_TACK. */
  const visibleChannels = createMemo(() => {
    const all = data().channels;
    const q = searchTerm().toLowerCase().trim();
    if (!q) {return all;}
    const terms = q.includes(' ') ? q.split(/\s+/).filter(Boolean) : [q];
    return all.filter((ch) => {
      const hay = ch.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  });

  /* ---- rows -------------------------------------------------------------------------------- */

  interface ButtonRow {
    channel: string;
    /** Value at the cursor, held forward. null = nothing reported at or before the cursor. */
    value: number | null;
    /** Went above zero at any point inside the sparkline window. */
    active: boolean;
    /** off / on / warn (held > 20s) / alert (held > 60s). */
    led: ButtonLedState;
    /** How long it has been continuously engaged at the cursor; null when not engaged. */
    heldMs: number | null;
    /** Most recent moment it was ON at or before the cursor; null if never on in the window. */
    lastMs: number | null;
    slice: { ts: number[]; values: number[] };
  }

  /**
   * The rows to draw, with anything that fired inside the sparkline window floated to the top.
   *
   * A boat can carry a lot of buttons and almost all of them are idle almost all of the time, so the
   * interesting handful would otherwise be scattered down a list you have to scroll. Same idea as the
   * schedule page pulling the live event out of the timeline. Active rows keep their own alphabetical order
   * so the group does not reshuffle within itself on every poll.
   *
   * The slices are built ONCE here and handed to the renderer, rather than each row recomputing its own for
   * both the ordering test and its sparkline.
   */
  const rows = createMemo<ButtonRow[]>(() => {
    const d = data();
    const cursor = cursorMs();
    const out = visibleChannels().map((channel) => {
      const slice = windowSlice(d, channel, cursor, SPARKLINE_WINDOW_MS);
      let active = false;
      for (const v of slice.values) {
        if (Number.isFinite(v) && v > 0) {
          active = true;
          break;
        }
      }
      return {
        channel,
        value: valueAt(d, channel, cursor),
        active,
        led: ledStateFor(d, channel, cursor),
        heldMs: engagedForMs(d, channel, cursor),
        lastMs: lastPressedMs(d, channel, cursor),
        slice,
      };
    });
    /**
     * MOST RECENTLY PRESSED FIRST.
     *
     * A button still being held has `lastMs` at the cursor, so it naturally leads - no separate rule is
     * needed to keep engaged buttons at the top. Channels never pressed in the window have no timestamp
     * and fall to the bottom together.
     *
     * Ties are broken by LED severity then name. Ties are common and not an edge case: every currently
     * engaged button shares the same `lastMs`, and at 1s buckets so does anything pressed in the same
     * second. Severity first means a stuck button leads its own second; the alphabetical last resort is
     * what stops the never-pressed block reshuffling on every poll.
     */
    const severity = (r: ButtonRow) => (r.led === 'alert' ? 0 : r.led === 'warn' ? 1 : r.led === 'on' ? 2 : 3);
    return out.sort((a, b) => {
      if (a.lastMs !== b.lastMs) {
        if (a.lastMs === null) {return 1;}
        if (b.lastMs === null) {return -1;}
        return b.lastMs - a.lastMs;
      }
      const sa = severity(a);
      const sb = severity(b);
      if (sa !== sb) {return sa - sb;}
      return a.channel < b.channel ? -1 : a.channel > b.channel ? 1 : 0;
    });
  });

  const activeCount = createMemo(() => rows().filter((r) => r.active).length);
  const stuckCount = createMemo(() => rows().filter((r) => r.led === 'warn' || r.led === 'alert').length);

  /* ---- render ------------------------------------------------------------------------------ */

  /**
   * LED state. Exactly 1 is the ordinary "pressed" and reads green; anything else above zero is still a
   * press but not a value we model, so it reads yellow rather than being rounded into the green case.
   */
  /**
   * Colour is driven by HOW LONG the button has been held, not by its value: green while a press looks
   * normal, yellow past 20s, red past a minute. An unheld or never-reported channel stays an outline.
   */
  const ledClass = (state: ButtonLedState) =>
    state === 'off' ? 'buttons-page__led' : `buttons-page__led buttons-page__led--${state}`;

  const formatHeld = (ms: number) => {
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  };

  const ledLabel = (row: ButtonRow) => {
    if (row.led === 'off') {
      if (row.value === null) {return 'no data';}
      return row.lastMs === null
        ? 'not pressed in this window'
        : `released — last pressed ${formatHeld(cursorMs() - row.lastMs)} ago`;
    }
    const held = row.heldMs === null ? '' : ` for ${formatHeld(row.heldMs)}`;
    if (row.led === 'alert') {return `engaged${held} — over ${ENGAGED_ALERT_MS / 1000}s`;}
    if (row.led === 'warn') {return `engaged${held} — over ${ENGAGED_WARN_MS / 1000}s`;}
    return `engaged${held}`;
  };

  return (
    <div class={`buttons-page${isFleet() ? '' : ' buttons-page--no-source'}`}>
      <div class="buttons-page__toolbar">
        <Show when={isFleet()}>
          <div class="buttons-page__source">
            <label for="buttons-source-select">Source</label>
            <select
              id="buttons-source-select"
              value={chosenSource()}
              onChange={(e) => setChosenSource(e.currentTarget.value)}
              aria-label="Select source"
            >
              <Show when={sources().length === 0}>
                <option value="">No sources</option>
              </Show>
              <For each={sources()}>{(s) => <option value={s}>{s}</option>}</For>
            </select>
          </div>
        </Show>

        <div class="buttons-page__search">
          <FiSearch class="buttons-page__search-icon" size={14} />
          <input
            type="search"
            value={searchTerm()}
            onInput={(e) => setSearchTerm(e.currentTarget.value)}
            placeholder="Filter by channel name…"
            aria-label="Filter buttons"
          />
        </div>

        <div class="buttons-page__record-count">
          <Show when={busy()}>
            <FiRefreshCw
              class="buttons-page__refresh-icon"
              size={13}
              aria-label="Updating"
            />
          </Show>
          <span>
            {loading()
              ? '—'
              : `${visibleChannels().length} button(s)${activeCount() > 0 ? ` · ${activeCount()} active` : ''}${
                stuckCount() > 0 ? ` · ${stuckCount()} held` : ''
              }`}
          </span>
        </div>
      </div>

      <Show when={loading()}>
        <div class="buttons-page__loading">Loading buttons… <span class="buttons-page__stage">{stage()}</span></div>
      </Show>

      <Show when={!loading() && loadError()}>
        <div class="buttons-page__error" role="alert">{loadError()}</div>
      </Show>

      <Show when={!loading() && !loadError() && data().channels.length === 0}>
        {/* Distinguish "nothing to show yet" from "looked, found none". The first is a context that has not
            arrived (no project, no dataset, no boat picked) and resolves itself; the second is an answer. */}
        <div class="buttons-page__empty">
          <Show
            when={stage().startsWith('waiting for')}
            fallback={
              <>
                No buttons pressed in the last 15 minutes.
              </>
            }
          >
            Waiting for {stage().replace('waiting for ', '')}…
          </Show>
        </div>
      </Show>

      {/* Channels were discovered but the value read came back with no usable samples. Without this the
          page shows a full list of correctly-named rows, every LED dark and every sparkline "-", which
          reads as "the feature is broken" rather than "the read returned nothing". */}
      <Show when={!loading() && !loadError() && data().channels.length > 0 && data().ts.length === 0}>
        <div class="buttons-page__empty">
          {data().channels.length} button(s) were pressed in this window, but the read returned no
          samples for them.
        </div>
      </Show>

      <Show when={!loading() && !loadError() && data().channels.length > 0 && visibleChannels().length === 0}>
        <div class="buttons-page__empty">No buttons match your filter.</div>
      </Show>

      <Show when={!loading() && !loadError() && data().ts.length > 0 && visibleChannels().length > 0}>
        <div class="buttons-page__main">
          <div class="buttons-page__table-wrapper">
            <table class="buttons-page__table" aria-label="Buttons">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>State</th>
                  <th>Last 20s</th>
                </tr>
              </thead>
              <tbody>
                <For each={rows()}>
                  {(row) => (
                    <tr
                      class={`button-row${row.active ? ' button-row--active' : ''}${
                        row.led === 'warn' || row.led === 'alert' ? ` button-row--${row.led}` : ''
                      }`}
                    >
                      <td class="button-row__channel">{row.channel}</td>
                      <td class="button-row__led">
                        <span
                          class={ledClass(row.led)}
                          role="img"
                          title={ledLabel(row)}
                          aria-label={`${row.channel}: ${ledLabel(row)}`}
                        />
                      </td>
                      <td class="button-row__sparkline">
                        <ButtonSparkline
                          ts={row.slice.ts}
                          values={row.slice.values}
                          cursorMs={cursorMs()}
                          windowMs={SPARKLINE_WINDOW_MS}
                        />
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </Show>
    </div>
  );
}
